/**
 * Load published + local draft layouts for slots/table runtime.
 */
import {
  DEFAULT_SLOTS_LAYOUT,
  DEFAULT_TABLE_LAYOUTS,
  defaultLayoutBundle,
  sanitizeSlotsLayout,
  sanitizeTableLayout,
  slotsLayoutToCssVars,
  tableLayoutKey,
  type LayoutBundle,
  type SeatLayoutPoint,
  type SlotsLayoutV1,
  type TableLayoutV1,
} from "@shared/admin-layouts";
import type { SeatPos } from "./table-layout";
import {
  SEAT_POS_2,
  SEAT_POS_2_V,
  SEAT_POS_6,
  SEAT_POS_6_V,
  SEAT_POS_9,
  SEAT_POS_9_V,
} from "./table-layout";

const LS_DRAFT = "poker_admin_layout_draft_v1";
const LS_CACHE = "poker_layout_published_v1";

export function readDraftBundle(): LayoutBundle | null {
  try {
    const raw = localStorage.getItem(LS_DRAFT);
    if (!raw) return null;
    return parseBundle(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeDraftBundle(bundle: LayoutBundle): void {
  try {
    localStorage.setItem(LS_DRAFT, JSON.stringify(bundle));
  } catch {
    /* ignore */
  }
}

export function clearDraftBundle(): void {
  try {
    localStorage.removeItem(LS_DRAFT);
  } catch {
    /* ignore */
  }
}

function parseBundle(raw: unknown): LayoutBundle {
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<LayoutBundle>;
  const tables: Record<string, TableLayoutV1> = { ...DEFAULT_TABLE_LAYOUTS };
  if (o.tables && typeof o.tables === "object") {
    for (const [k, v] of Object.entries(o.tables)) {
      tables[k] = sanitizeTableLayout(v, k);
    }
  }
  return {
    slots: sanitizeSlotsLayout(o.slots ?? DEFAULT_SLOTS_LAYOUT),
    tables,
    updatedAt: o.updatedAt,
    updatedBy: o.updatedBy,
  };
}

/** Prefer admin draft (preview) → published cache → defaults */
export function resolveSlotsLayout(): SlotsLayoutV1 {
  const draft = readDraftBundle();
  if (draft?.slots) return draft.slots;
  try {
    const raw = localStorage.getItem(LS_CACHE);
    if (raw) return parseBundle(JSON.parse(raw)).slots;
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_SLOTS_LAYOUT };
}

export function resolveTableLayout(
  maxPlayers: number,
  vertical: boolean,
): SeatPos[] {
  const key = tableLayoutKey(maxPlayers, vertical);
  const draft = readDraftBundle();
  const fromDraft = draft?.tables?.[key];
  if (fromDraft?.seats?.length) {
    return fromDraft.seats.map((s) => toSeatPos(s));
  }
  try {
    const raw = localStorage.getItem(LS_CACHE);
    if (raw) {
      const t = parseBundle(JSON.parse(raw)).tables[key];
      if (t?.seats?.length) return t.seats.map((s) => toSeatPos(s));
    }
  } catch {
    /* ignore */
  }
  // Code defaults
  if (maxPlayers <= 2) return vertical ? SEAT_POS_2_V : SEAT_POS_2;
  if (maxPlayers >= 9) return vertical ? SEAT_POS_9_V : SEAT_POS_9;
  return vertical ? SEAT_POS_6_V : SEAT_POS_6;
}

function toSeatPos(s: SeatLayoutPoint): SeatPos {
  return {
    left: `${s.left}%`,
    top: `${s.top}%`,
    label: s.label,
  };
}

export function slotsLayoutStyle(
  layout?: SlotsLayoutV1,
): Record<string, string> {
  return slotsLayoutToCssVars(layout || resolveSlotsLayout());
}

/** Fetch published layouts from Worker; cache for offline/restart */
export async function fetchPublishedLayouts(): Promise<LayoutBundle> {
  try {
    const r = await fetch("/api/layouts");
    if (!r.ok) throw new Error("layouts fetch failed");
    const data = (await r.json()) as { layouts?: LayoutBundle };
    const bundle = parseBundle(data.layouts || defaultLayoutBundle());
    try {
      localStorage.setItem(LS_CACHE, JSON.stringify(bundle));
    } catch {
      /* ignore */
    }
    return bundle;
  } catch {
    try {
      const raw = localStorage.getItem(LS_CACHE);
      if (raw) return parseBundle(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    return defaultLayoutBundle();
  }
}
